import React, { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';
import { Modal, ModalTitle, ModalContent, ModalActions, Button, CircularLoader, IconUser24 } from '@dhis2/ui';
import { POSITION_ATTRIBUTES } from '../api/constants';

const StaffViewModal = ({ open, onClose, staffData }) => {
    const engine = useDataEngine();
    const [loadingPosition, setLoadingPosition] = useState(false);
    const [positionDetails, setPositionDetails] = useState(null);

    useEffect(() => {
        if (!open || !staffData) return;

        if (staffData.positionDetails) {
            setPositionDetails(staffData.positionDetails);
            return;
        }

        if (staffData.positionId) {
            const fetchPosition = async () => {
                setLoadingPosition(true);
                try {
                    const { position } = await engine.query({
                        position: {
                            resource: `trackedEntityInstances/${staffData.positionId}`,
                            params: { fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]' }
                        }
                    });
                    if (position) {
                        const attrs = {};
                        position.attributes.forEach(a => { attrs[a.attribute] = a.value; });
                        setPositionDetails({
                            title: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Position',
                            code: attrs[POSITION_ATTRIBUTES.POSITION_ID] || '',
                            incumbent: attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || null,
                            status: attrs[POSITION_ATTRIBUTES.POSITION_STATUS] || 'Unknown'
                        });
                    }
                } catch (e) {
                    console.warn('Failed to fetch position details:', e);
                } finally {
                    setLoadingPosition(false);
                }
            };
            fetchPosition();
            return;
        }

        if (staffData.userId) {
            const fetchPositionByIncumbent = async () => {
                setLoadingPosition(true);
                try {
                    const { positions } = await engine.query({
                        positions: {
                            resource: 'trackedEntityInstances',
                            params: {
                                trackedEntityType: 'UGGo0Ebjl0E', 
                                filter: `V66IFsw5kq6:eq:${staffData.userId}`,
                                fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
                                pageSize: 1,
                                ouMode: 'ALL'  
                            }
                        }
                    });
                    const pos = positions?.trackedEntityInstances?.[0];
                    if (pos) {
                        const attrs = {};
                        pos.attributes.forEach(a => { attrs[a.attribute] = a.value; });
                        setPositionDetails({
                            title: attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Position',
                            code: attrs[POSITION_ATTRIBUTES.POSITION_ID] || '',
                            incumbent: attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT] || null,
                            status: attrs[POSITION_ATTRIBUTES.POSITION_STATUS] || 'Unknown'
                        });
                    }
                } catch (e) {
                    console.warn('Failed to fetch position by incumbent:', e);
                } finally {
                    setLoadingPosition(false);
                }
            };
            fetchPositionByIncumbent();
        }
    }, [open, staffData, engine]);

    if (!staffData) return null;

    const fullName = `${staffData.firstName || ''} ${staffData.surname || ''}`.trim() || '—';
    const departments = staffData.organisationUnits?.map(ou => ou.name).join(', ') || '—';
    const roles = staffData.userRoles?.map(r => r.name).join(', ') || '—';

    const positionTitle = positionDetails?.title || staffData.positionDetails?.title || '—';
    const positionCode = positionDetails?.code || staffData.positionDetails?.code || '';

    // ✅ EXTRACT GENDER FROM TEI ATTRIBUTES
    const genderAttr = staffData.attributes?.find(attr => attr.attribute === 'HAOvVNYm8Lx');
    const genderDisplay = genderAttr?.value 
        ? genderAttr.value.charAt(0).toUpperCase() + genderAttr.value.slice(1).toLowerCase() 
        : '—';

    return (
        <Modal open={open} onClose={onClose} large>
            <ModalTitle>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '1.2rem' }}><IconUser24 /></span>
                    {fullName}
                </span>
            </ModalTitle>
            <ModalContent>
                {loadingPosition ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <CircularLoader small /> Loading position details...
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        {/* Left: Personal */}
                        <div>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                                Personal Information
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Full Name</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{fullName}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Username</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{staffData.username || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Email</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{staffData.email || '—'}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Phone</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{staffData.phoneNumber || '—'}</div>
                                </div>
                                {/* ✅ GENDER ADDED HERE */}
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Gender</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{genderDisplay}</div>
                                </div>
                            </div>
                        </div>
                        {/* Right: Work */}
                        <div>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#1976d2', fontSize: '0.9rem', textTransform: 'uppercase' }}>
                                Work Information
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Department(s)</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{departments}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Role(s)</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>{roles}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Assigned Position</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>
                                        {positionTitle}
                                        {positionCode && <span style={{ fontSize: '0.8rem', color: '#888', marginLeft: '8px' }}>({positionCode})</span>}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: '500' }}>Position Status</div>
                                    <div style={{ padding: '0.4rem 0', fontSize: '1rem' }}>
                                        {positionDetails?.status || '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </ModalContent>
            <ModalActions>
                <button
                  style={{
                        backgroundColor: '#d53713',
                        border: '1px solid #ddd',
                        borderRadius: '12px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        color: '#fff'
                      }}
                  onClick={onClose}>
                    Close
                </button>
            </ModalActions>
        </Modal>
    );
};

export default StaffViewModal;